import type { GatewayBrowserClient } from "../gateway.ts";
import type { LogEntry, LogLevel } from "../types.ts";
import {
  applyScopeToParsedLine,
  extractMetaFromLogObject,
} from "../../../../src/logging/log-scope.js";

export type LogsState = {
  client: GatewayBrowserClient | null;
  connected: boolean;
  logsLoading: boolean;
  logsError: string | null;
  logsCursor: number | null;
  logsFile: string | null;
  logsEntries: LogEntry[];
  logsTruncated: boolean;
  logsLastFetchAt: number | null;
  logsLimit: number;
  logsMaxBytes: number;
  logsMatched?: number | null;
  logsScanned?: number | null;
  /** False when gateway rejects extended logs.tail filter params (older build). */
  logsTailServerFilters?: boolean;
};

export type LogsTailRequestFilters = {
  subsystemPrefixes?: string[];
  sessionKeyPrefix?: string;
  levels?: LogLevel[];
  needle?: string;
  sinceMs?: number;
  includePlatform?: boolean;
};

const LOG_BUFFER_LIMIT = 2000;
const LEVELS = new Set<LogLevel>(["trace", "debug", "info", "warn", "error", "fatal"]);

function parseMaybeJsonString(value: unknown) {
  if (typeof value !== "string") {
    return null;
  }
  const trimmed = value.trim();
  if (!trimmed.startsWith("{") || !trimmed.endsWith("}")) {
    return null;
  }
  try {
    const parsed = JSON.parse(trimmed) as unknown;
    if (!parsed || typeof parsed !== "object") {
      return null;
    }
    return parsed as Record<string, unknown>;
  } catch {
    return null;
  }
}

function normalizeLevel(value: unknown): LogLevel | null {
  if (typeof value !== "string") {
    return null;
  }
  const lowered = value.toLowerCase() as LogLevel;
  return LEVELS.has(lowered) ? lowered : null;
}

export function parseLogLine(line: string): LogEntry {
  if (!line.trim()) {
    return { raw: line, message: line };
  }
  try {
    const obj = JSON.parse(line) as Record<string, unknown>;
    const meta =
      obj && typeof obj._meta === "object" && obj._meta !== null
        ? (obj._meta as Record<string, unknown>)
        : null;
    const time =
      typeof obj.time === "string" ? obj.time : typeof meta?.date === "string" ? meta.date : null;
    const level = normalizeLevel(meta?.logLevelName ?? meta?.level);

    const metaObject = extractMetaFromLogObject(obj);
    const contextCandidate =
      typeof obj["0"] === "string" ? obj["0"] : typeof meta?.name === "string" ? meta?.name : null;
    const contextObj = parseMaybeJsonString(contextCandidate);
    let subsystem: string | null = null;
    if (contextObj) {
      if (typeof contextObj.subsystem === "string") {
        subsystem = contextObj.subsystem;
      } else if (typeof contextObj.module === "string") {
        subsystem = contextObj.module;
      }
    }
    if (!subsystem && metaObject) {
      if (typeof metaObject.subsystem === "string") {
        subsystem = metaObject.subsystem;
      } else if (typeof metaObject.module === "string") {
        subsystem = metaObject.module;
      }
    }
    if (!subsystem && contextCandidate && contextCandidate.length < 120) {
      subsystem = contextCandidate;
    }

    let message: string | null = null;
    if (typeof obj["1"] === "string") {
      message = obj["1"];
    } else if (!contextObj && typeof obj["0"] === "string") {
      message = obj["0"];
    } else if (typeof obj.message === "string") {
      message = obj.message;
    }

    const scoped = applyScopeToParsedLine({
      time: time ?? undefined,
      level: level ?? undefined,
      subsystem: subsystem ?? undefined,
      message: message ?? line,
      raw: line,
      metaObject: metaObject ?? undefined,
    });

    return {
      raw: line,
      time,
      level,
      subsystem,
      message: message ?? line,
      meta: meta ?? undefined,
      scope: scoped.scope ?? null,
      agent: scoped.agent ?? null,
      kind: scoped.kind ?? null,
      id: scoped.id ?? null,
      sessionKey: scoped.sessionKey ?? null,
      area: scoped.area ?? null,
      runId: scoped.runId ?? null,
      correlationId: scoped.correlationId ?? null,
    };
  } catch {
    return { raw: line, message: line };
  }
}

function isUnsupportedLogsTailFiltersError(err: unknown): boolean {
  const message = String(err);
  return message.includes("invalid logs.tail params") && message.includes("unexpected property");
}

function buildLogsTailRpcParams(
  state: LogsState,
  opts: { reset?: boolean; filters?: LogsTailRequestFilters },
  useServerFilters: boolean,
) {
  const filters = opts.filters;
  return {
    cursor: opts.reset ? undefined : (state.logsCursor ?? undefined),
    limit: state.logsLimit,
    maxBytes: state.logsMaxBytes,
    ...(useServerFilters && filters?.subsystemPrefixes?.length
      ? { subsystemPrefixes: filters.subsystemPrefixes }
      : {}),
    ...(useServerFilters && filters?.sessionKeyPrefix
      ? { sessionKeyPrefix: filters.sessionKeyPrefix }
      : {}),
    ...(useServerFilters && filters?.levels?.length ? { levels: filters.levels } : {}),
    ...(useServerFilters && filters?.needle?.trim() ? { needle: filters.needle.trim() } : {}),
    ...(useServerFilters && filters?.sinceMs != null ? { sinceMs: filters.sinceMs } : {}),
    ...(useServerFilters && filters?.includePlatform != null
      ? { includePlatform: filters.includePlatform }
      : {}),
  };
}

export async function loadLogs(
  state: LogsState,
  opts?: { reset?: boolean; quiet?: boolean; filters?: LogsTailRequestFilters },
) {
  if (!state.client || !state.connected) {
    return;
  }
  if (state.logsLoading && !opts?.quiet) {
    return;
  }
  if (!opts?.quiet) {
    state.logsLoading = true;
  }
  state.logsError = null;
  const useServerFilters = state.logsTailServerFilters !== false;
  try {
    let res: unknown;
    try {
      res = await state.client.request(
        "logs.tail",
        buildLogsTailRpcParams(state, opts ?? {}, useServerFilters),
      );
    } catch (err) {
      if (useServerFilters && isUnsupportedLogsTailFiltersError(err)) {
        state.logsTailServerFilters = false;
        res = await state.client.request(
          "logs.tail",
          buildLogsTailRpcParams(state, opts ?? {}, false),
        );
      } else {
        throw err;
      }
    }
    const payload = res as {
      file?: string;
      cursor?: number;
      size?: number;
      lines?: unknown;
      truncated?: boolean;
      reset?: boolean;
      matched?: number;
      scanned?: number;
    };
    const lines = Array.isArray(payload.lines)
      ? payload.lines.filter((line) => typeof line === "string")
      : [];
    const entries = lines.map(parseLogLine);
    const shouldReset = Boolean(opts?.reset || payload.reset || state.logsCursor == null);
    state.logsEntries = shouldReset
      ? entries
      : [...state.logsEntries, ...entries].slice(-LOG_BUFFER_LIMIT);
    if (typeof payload.cursor === "number") {
      state.logsCursor = payload.cursor;
    }
    if (typeof payload.file === "string") {
      state.logsFile = payload.file;
    }
    state.logsTruncated = Boolean(payload.truncated);
    state.logsMatched = typeof payload.matched === "number" ? payload.matched : null;
    state.logsScanned = typeof payload.scanned === "number" ? payload.scanned : null;
    state.logsLastFetchAt = Date.now();
  } catch (err) {
    state.logsError = String(err);
  } finally {
    if (!opts?.quiet) {
      state.logsLoading = false;
    }
  }
}
